import { appConstants } from '../app-constants';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidatorFn } from '@angular/forms';
import { Router } from '@angular/router';
import { LanguageService } from '../services/language.service';
import { environment } from '../../environments/environment';
import { CountryCode } from 'src/app/graphql/gql';
import { AuthTokenService } from '../services/auth-token.service';

interface SelectOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.css'],
  standalone: false
})
export class SignupComponent implements OnInit {
  form: FormGroup;
  submitted = false;
  authenticityToken!: string;
  public countryCodes = Object.values(CountryCode);
  public countryStates = [{id: 0, name: 'Other'}];
  serverErrors: {[key: string]: string} = {};
  showConfirmationMessage = false;
  // Options for PrimeNG select components
  titleOptions: SelectOption[] = [
    { label: 'Mr', value: 'mr' },
    { label: 'Ms', value: 'ms' }
  ];

  countryOptions: SelectOption[] = [];
  stateOptions: SelectOption[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private authTokenService: AuthTokenService,
    private languageService: LanguageService
  ) {
    // Initialize form in constructor to avoid undefined errors
    this.form = this.formBuilder.group({
      password: ['', Validators.compose([Validators.required, Validators.minLength(12), Validators.pattern("^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{4,}$")])],
      password_confirmation: ['', Validators.required],
      firstname: ['', Validators.required],
      lastname: ['', Validators.required],
      email: ['', Validators.required],
      company: ['', Validators.required],
      title: [''],
      street: ['', Validators.required],
      zip: ['', Validators.required],
      city: ['', Validators.required],
      country: [''],
      country_state: [''],
      phone: ['', Validators.compose([Validators.required, Validators.pattern(/^\+[0-9]+\.[0-9]+$/)])],
      cell_phone: [''],
      commercial_register_number: [''],
      id_card_number: [''],
      tax_number_company: [''],
      tax_number_personal: [''],
      passphrase: ['', Validators.required],
      answer: ['', Validators.required],
      accept_dpa: [false, Validators.requiredTrue],
      accept_privacy_policy: [false, Validators.requiredTrue]
    });
  }

  get hasServerErrors(): boolean {
    return Object.keys(this.serverErrors).length > 0;
  }

  get serverErrorKeys(): string[] {
    return Object.keys(this.serverErrors);
  }

  async ngOnInit() {
    this.form.addValidators(this.matchValidator('password', 'password_confirmation'));
    this.authenticityToken = await this.authTokenService.getAuthenticityToken(true);

    // Initialize country options
    this.countryOptions = this.countryCodes.map(code => ({
      label: code,
      value: code
    }));

    // Initialize state options
    this.updateStateOptions();
  }

  private updateStateOptions() {
    this.stateOptions = this.countryStates.map(state => ({
      label: state.name,
      value: state.id.toString()
    }));
  }

  matchValidator(controlName: string, matchingControlName: string): ValidatorFn {
    return (abstractControl: AbstractControl) => {
        const control = abstractControl.get(controlName);
        const matchingControl = abstractControl.get(matchingControlName);

        if (matchingControl!.errors && !matchingControl!.errors?.['confirmedValidator']) {
            return null;
        }

        if (control!.value !== matchingControl!.value) {
          const error = { confirmedValidator: 'Passwords do not match.' };
          matchingControl!.setErrors(error);
          return error;
        } else {
          matchingControl!.setErrors(null);
          return null;
        }
    }
  }

  async onSubmit() {
    this.submitted = true;

    // Clear previous server errors
    this.serverErrors = {};

    // Clear server errors from form controls
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      if (control && control.errors && control.errors['serverError']) {
        const errors = { ...control.errors };
        delete errors['serverError'];
        control.setErrors(Object.keys(errors).length > 0 ? errors : null);
      }
    });

    // stop here if form is invalid
    if (this.form.invalid) {
      console.log('INVALID')
      return;
    }

    let body = encodeURIComponent("user[password]") + "=" + encodeURIComponent(this.form.value.password) + "&" +
               encodeURIComponent("user[password_confirmation]") + "=" + encodeURIComponent(this.form.value.password_confirmation) + "&" +
               encodeURIComponent("user[firstname]") + "=" + encodeURIComponent(this.form.value.firstname) + "&" +
               encodeURIComponent("user[lastname]") + "=" + encodeURIComponent(this.form.value.lastname) + "&" +
               encodeURIComponent("user[email]") + "=" + encodeURIComponent(this.form.value.email) + "&" +
               encodeURIComponent("user[company]") + "=" + encodeURIComponent(this.form.value.company) + "&" +
               encodeURIComponent("user[title]") + "=" + encodeURIComponent(this.form.value.title) + "&" +
               encodeURIComponent("user[street]") + "=" + encodeURIComponent(this.form.value.street) + "&" +
               encodeURIComponent("user[zip]") + "=" + encodeURIComponent(this.form.value.zip) + "&" +
               encodeURIComponent("user[city]") + "=" + encodeURIComponent(this.form.value.city) + "&" +
               encodeURIComponent("user[country]") + "=" + encodeURIComponent(this.form.value.country) + "&" +
               encodeURIComponent("user[country_state]") + "=" + encodeURIComponent(this.form.value.country_state) + "&" +
               encodeURIComponent("user[phone]") + "=" + encodeURIComponent(this.form.value.phone) + "&" +
               encodeURIComponent("user[cell_phone]") + "=" + encodeURIComponent(this.form.value.cell_phone) + "&" +
               encodeURIComponent("user[commercial_register_number]") + "=" + encodeURIComponent(this.form.value.commercial_register_number) + "&" +
               encodeURIComponent("user[id_card_number]") + "=" + encodeURIComponent(this.form.value.id_card_number) + "&" +
               encodeURIComponent("user[tax_number_company]") + "=" + encodeURIComponent(this.form.value.tax_number_company) + "&" +
               encodeURIComponent("user[tax_number_personal]") + "=" + encodeURIComponent(this.form.value.tax_number_personal) + "&" +
               encodeURIComponent("user[passphrase]") + "=" + encodeURIComponent(this.form.value.passphrase) + "&" +
               encodeURIComponent("user[answer]") + "=" + encodeURIComponent(this.form.value.answer) + "&" +
               encodeURIComponent("user[accept_dpa]") + "=" + encodeURIComponent(this.form.value.accept_dpa) + "&" +
               encodeURIComponent("user[accept_privacy_policy]") + "=" + encodeURIComponent(this.form.value.accept_privacy_policy)

    let response = await fetch(environment.legacyAppUrl + "/accounts/create_frontend", {
      "headers": {
        "accept": "application/json",
        "content-type": "application/x-www-form-urlencoded",
        "X-CSRF-Token": this.authenticityToken
      },
      "body": body,
      "method": "POST",
      "credentials": 'include'
    });

    this.handleResponse(response)
  }

  private async handleResponse(response: Response) {
    if(response.status == 201) {
      this.showConfirmationMessage = true;
    } else if(response.status == 200) {
      this.form.reset()
      this.submitted = false
      this.serverErrors = {};
    } else if(response.status == 422) {
      let response_json = await response.json();

      // Clear previous server errors
      this.serverErrors = {};

      // Set errors on individual form controls
      Object.keys(response_json.errors).forEach(fieldName => {
        const control = this.form.get(fieldName);
        if (control) {
          control.setErrors({ serverError: response_json.errors[fieldName] });
        } else {
          // Store errors for fields that don't exist in the form
          this.serverErrors[fieldName] = response_json.errors[fieldName];
        }
      });
    }
  }

  async fetchCountryStates(event: any) {
    const countryValue = event.value;
    let response = await fetch(environment.legacyAppUrl + `/countries/${countryValue}/states`, {
      "headers": {
        "accept": "application/json",
        "X-CSRF-Token": await this.authTokenService.getAuthenticityToken(true)
      },
      "method": "GET",
    })
    const response_json = await response.json()
    this.countryStates = response_json
    this.updateStateOptions();
  }

  logoUrl(): string {
    return appConstants.LOGO_URL;
  }

  privacyPolicyUrl(): string {
    return appConstants.WEBSITE_URL + '/' + this.languageService.getCurrentLanguage() + '/privacy-policy'
  }

  dataProcessingAgreementUrl(): string {
    const file = this.languageService.getCurrentLanguage() === 'de' ?
    'Auftragsdatenverarbeitungsvertrag Key-Systems_Mai 2018.pdf' :
    'Data Processing Agreement Key-Systems_May 2018.pdf';

    return environment.legacyAppUrl + '/papers/' + file
  }
}
